import React, { useState, useEffect, useRef } from 'react';
import { Phone, Mic, MicOff, Volume2, VolumeX, Grip, UserPlus, Video } from 'lucide-react';
import { RetellWebClient } from 'retell-client-js-sdk';
import { createWebCall } from '../services/retellService';

const ChatDemo: React.FC = () => {
    const [callStatus, setCallStatus] = useState<'idle' | 'connecting' | 'active'>('idle');
    const [duration, setDuration] = useState(0);
    const [isMuted, setIsMuted] = useState(false);
    const [isSpeaker, setIsSpeaker] = useState(true);
    const [isAgentTalking, setIsAgentTalking] = useState(false);
    
    // Retell Client Reference
    const retellWebClient = useRef<RetellWebClient | null>(null);

    // Initialize Retell Client
    useEffect(() => {
        const client = new RetellWebClient();
        retellWebClient.current = client;

        // Event Listeners
        client.on('call_started', () => {
            console.log('Call started');
            setCallStatus('active');
        });

        client.on('call_ended', () => {
            console.log('Call ended');
            setCallStatus('idle');
            setIsAgentTalking(false);
            setDuration(0);
        });

        client.on('agent_start_talking', () => {
            setIsAgentTalking(true);
        });

        client.on('agent_stop_talking', () => {
            setIsAgentTalking(false);
        });

        client.on('error', (error) => {
            console.error('Retell Client Error:', error);
            setCallStatus('idle');
        });

        return () => {
            if (client) {
                client.stopCall();
            }
        };
    }, []);

    // Timer Logic
    useEffect(() => {
        let interval: any;
        if (callStatus === 'active') {
            interval = setInterval(() => {
                setDuration(prev => prev + 1);
            }, 1000);
        } else {
            setDuration(0);
        }
        return () => clearInterval(interval);
    }, [callStatus]);

    const startCall = async () => {
        if (!retellWebClient.current) return;
        
        try {
            // Check Microphone Permissions explicitly
            try {
                await navigator.mediaDevices.getUserMedia({ audio: true });
            } catch (micError) {
                alert("Microphone access denied. Please allow microphone access to use the demo.");
                return;
            }

            setCallStatus('connecting');
            
            // Get Access Token
            const data = await createWebCall();
            if (!data.access_token) {
                throw new Error("Server response missing access_token");
            }

            // Start Call
            await retellWebClient.current.startCall({
                accessToken: data.access_token,
            });
            
        } catch (error) {
            console.error("Failed to start call:", error);
            setCallStatus('idle');
            // Show alert for debugging
            alert(`Call Failed: ${error instanceof Error ? error.message : "Unknown error"}`);
        }
    };

    const endCall = () => {
        if (retellWebClient.current) {
            retellWebClient.current.stopCall();
        }
    };

    const toggleMute = () => {
        setIsMuted(!isMuted);
    };

    const formatTime = (secs: number) => {
        const mins = Math.floor(secs / 60);
        const remainingSecs = secs % 60;
        return `${mins.toString().padStart(2, '0')}:${remainingSecs.toString().padStart(2, '0')}`;
    };

    return (
        <div className="mx-auto w-[350px] h-[650px] bg-black rounded-[40px] border-[8px] border-zinc-800 relative overflow-hidden shadow-2xl flex flex-col font-sans select-none">
            {/* Background */}
            <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 via-zinc-950 to-black z-0"></div>
            
            {/* Glossy Reflection */}
            <div className="absolute top-0 left-0 right-0 h-1/3 bg-gradient-to-b from-white/5 to-transparent z-0 pointer-events-none"></div>

            {/* Status Bar */}
            <div className="relative z-10 px-6 pt-4 flex justify-between items-center text-[10px] font-medium text-white">
                <span>9:41</span>
                <div className="flex items-center gap-1.5">
                    <div className="flex gap-0.5 items-end h-2.5">
                        <div className="w-0.5 h-1 bg-white rounded-full"></div>
                        <div className="w-0.5 h-1.5 bg-white rounded-full"></div>
                        <div className="w-0.5 h-2 bg-white rounded-full"></div>
                        <div className="w-0.5 h-2.5 bg-white/30 rounded-full"></div>
                    </div>
                    <span>5G</span>
                    <div className="w-5 h-2.5 border border-white/30 rounded-[3px] p-[1px] relative">
                        <div className="h-full bg-white rounded-[1px] w-[80%]"></div>
                    </div>
                </div>
            </div>

            {/* Content Container */}
            <div className="relative z-10 flex-1 flex flex-col items-center pt-12 pb-10 px-6">
                
                {/* Caller Info */}
                <div className="flex flex-col items-center mb-auto w-full">
                    <div className="w-24 h-24 rounded-full bg-gradient-to-t from-zinc-700 to-zinc-600 mb-6 flex items-center justify-center shadow-lg relative">
                        {/* Avatar Image or Initials */}
                        <div className="text-3xl font-medium text-white/90">HN</div>
                        
                        {/* Ring animation when talking */}
                        {isAgentTalking && (
                            <>
                                <div className="absolute inset-0 rounded-full border border-orange-500/50 animate-ping opacity-50"></div>
                                <div className="absolute inset-[-8px] rounded-full border border-orange-500/30 animate-pulse"></div>
                            </>
                        )}
                    </div>
                    
                    <h2 className="text-2xl font-bold text-white tracking-tight mb-2">Hire Nobody AI</h2>
                    
                    <p className="text-zinc-400 text-sm font-medium animate-pulse">
                        {callStatus === 'idle' ? 'Mobile' : callStatus === 'connecting' ? 'Calling...' : formatTime(duration)}
                    </p>
                </div>

                {/* Main Action Area */}
                <div className="w-full mt-auto space-y-8">
                    
                    {/* In-Call Controls Grid */}
                    <div className={`grid grid-cols-3 gap-y-6 gap-x-4 transition-all duration-500 ${callStatus === 'active' ? 'opacity-100 translate-y-0' : 'opacity-30 translate-y-4 pointer-events-none'}`}>
                        <div className="flex flex-col items-center gap-2">
                            <button 
                                onClick={toggleMute}
                                className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${isMuted ? 'bg-white text-black' : 'bg-zinc-800/80 text-white backdrop-blur-md'}`}
                            >
                                {isMuted ? <MicOff size={24} /> : <Mic size={24} />}
                            </button>
                            <span className="text-[10px] text-white/80">mute</span>
                        </div>
                        <div className="flex flex-col items-center gap-2">
                            <button className="w-14 h-14 rounded-full bg-zinc-800/80 backdrop-blur-md flex items-center justify-center text-white/50">
                                <Grip size={24} />
                            </button>
                            <span className="text-[10px] text-white/50">keypad</span>
                        </div>
                        <div className="flex flex-col items-center gap-2">
                            <button 
                                onClick={() => setIsSpeaker(!isSpeaker)}
                                className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${isSpeaker ? 'bg-white text-black' : 'bg-zinc-800/80 text-white backdrop-blur-md'}`}
                            >
                                {isSpeaker ? <Volume2 size={24} /> : <VolumeX size={24} />}
                            </button>
                            <span className="text-[10px] text-white/80">speaker</span>
                        </div>
                        <div className="flex flex-col items-center gap-2">
                            <button className="w-14 h-14 rounded-full bg-zinc-800/80 backdrop-blur-md flex items-center justify-center text-white/50">
                                <UserPlus size={24} />
                            </button>
                            <span className="text-[10px] text-white/50">add call</span>
                        </div>
                         <div className="flex flex-col items-center gap-2">
                            <button className="w-14 h-14 rounded-full bg-zinc-800/80 backdrop-blur-md flex items-center justify-center text-white/50">
                                <Video size={24} />
                            </button>
                            <span className="text-[10px] text-white/50">FaceTime</span>
                        </div>
                         <div className="flex flex-col items-center gap-2">
                            <button className="w-14 h-14 rounded-full bg-zinc-800/80 backdrop-blur-md flex items-center justify-center text-white/50">
                                <UserPlus size={24} />
                            </button>
                            <span className="text-[10px] text-white/50">contacts</span>
                        </div>
                    </div>

                    {/* End / Start Call Button */}
                    <div className="flex justify-center pt-4">
                        {callStatus === 'idle' ? (
                            <button 
                                onClick={startCall}
                                className="w-16 h-16 rounded-full bg-green-500 flex items-center justify-center shadow-lg shadow-green-500/30 transform hover:scale-105 transition-all"
                            >
                                <Phone size={32} fill="currentColor" className="text-white" />
                            </button>
                        ) : (
                            <button 
                                onClick={endCall}
                                className="w-16 h-16 rounded-full bg-red-500 flex items-center justify-center shadow-lg shadow-red-500/30 transform hover:scale-105 transition-all"
                            >
                                <Phone size={32} fill="currentColor" className="text-white rotate-[135deg]" />
                            </button>
                        )}
                    </div>
                    
                </div>
            </div>
            
            {/* Home Indicator */}
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-32 h-1 bg-white/20 rounded-full z-20"></div>
        </div>
    );
};

export default ChatDemo;